/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { AxiosError } from 'axios';
import axiosConfig, { ApiRequestType } from 'axiosConfig/axiosConfig';
import decompressObject from 'components/common/Utils/decompressObject';
import {
  OriginType,
  PitStrategyDataResponse,
  RunType,
  SessionPitRoadAnalysisRequest,
  SessionPitRoadAnalysisResponse,
  SessionPitRoadSummaryResponse,
  SessionPitStrategyRequest,
  SessionPitSummaryRequest,
  SessionProgressionRequest,
  SessionProgressionResponse,
  SessionTimingScoringRequest,
  SessionTimingScoringResponse,
} from 'models/viewModels';

class HistoricalRaceServiceApi {
  async getReplaySessionTimingScoringData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<SessionTimingScoringResponse> {
    const sessionTimingScoringRequest: SessionTimingScoringRequest = {
      eventId: eventId,
      sessionType: sessionType,
      sessionId: sessionId,
    };

    const response = new Promise<SessionTimingScoringResponse>((resolve, reject) => {
      axiosConfig(ApiRequestType.StreamMetricsApi)
        .post('/replay/timingScoring', sessionTimingScoringRequest)
        .then((res) => {
          // Ternary is to address issue when API throws an error, and res.data is an exception instead of an encoded string
          resolve(res.data.statusCode ? res.data : decompressObject<SessionTimingScoringResponse>(res.data));
        })
        .catch((error) => {
          reject(new AxiosError(error.message, error.response?.status, error.response?.data));
        });
    });
    return response;
  }

  async getReplaySessionProgressionData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<SessionProgressionResponse> {
    const sessionProgressionRequestObject: SessionProgressionRequest = {
      eventId: eventId,
      sessionType: sessionType,
      sessionId: sessionId,
    };

    const response = new Promise<SessionProgressionResponse>((resolve, reject) => {
      axiosConfig(ApiRequestType.StreamMetricsApi)
        .post('/replay/raceProgression', sessionProgressionRequestObject)
        .then((res) => {
          // Ternary is to address issue when API throws an error, and res.data is an exception instead of an encoded string
          resolve(res.data.statusCode ? res.data : decompressObject<SessionProgressionResponse>(res.data));
        })
        .catch((error) => {
          reject(new AxiosError(error.message, error.response?.status, error.response?.data));
        });
    });
    return response;
  }

  async getReplaySessionPitRoadAnalysisData(
    eventId: string,
    sessionType: RunType
  ): Promise<SessionPitRoadAnalysisResponse> {
    const sessionPitRoadAnalysisRequest: SessionPitRoadAnalysisRequest = {
      eventId,
      sessionType,
    };
    const response = new Promise<SessionPitRoadAnalysisResponse>((resolve, reject) => {
      axiosConfig(ApiRequestType.StreamMetricsApi)
        .post('/replay/pitRoad/analysis', sessionPitRoadAnalysisRequest)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(new AxiosError(error.message, error.status));
        });
    });
    return response;
  }

  async getReplaySessionPitRoadSummaryData(
    eventId: string,
    sessionType: RunType,
    sessionId: string,
    originType: OriginType
  ): Promise<SessionPitRoadSummaryResponse> {
    const sessionPitRoadSummaryRequest: SessionPitSummaryRequest = {
      eventId,
      sessionType,
      sessionId,
      originType,
    };
    const response = new Promise<SessionPitRoadSummaryResponse>((resolve, reject) => {
      axiosConfig(ApiRequestType.StreamMetricsApi)
        .post('/replay/pitRoad/summary', sessionPitRoadSummaryRequest)
        .then((res) => {
          // Ternary is to address issue when API throws an error, and res.data is an exception instead of an encoded string
          resolve(res.data.statusCode ? res.data : decompressObject<SessionPitRoadSummaryResponse>(res.data));
        })
        .catch((error) => {
          reject(new AxiosError(error.message, error.code));
        });
    });
    return response;
  }

  async getReplayPitStrategyData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<PitStrategyDataResponse> {
    const pitStrategyRequest: SessionPitStrategyRequest = {
      eventId,
      sessionType,
      sessionId,
    };
    const response = new Promise<PitStrategyDataResponse>((resolve, reject) => {
      axiosConfig(ApiRequestType.StreamMetricsApi)
        .post('/replay/pitRoad/strategy', pitStrategyRequest)
        .then((res) => {
          // Ternary is to address issue when API throws an error, and res.data is an exception instead of an encoded string
          resolve(res.data.statusCode ? res.data : decompressObject<PitStrategyDataResponse>(res.data));
        })
        .catch((error) => {
          reject(new AxiosError(error.message, error.code));
        });
    });
    return response;
  }
}

export const historicalRaceServiceApi = new HistoricalRaceServiceApi();
